import { useParams, useNavigate } from "react-router-dom";
import { sanitizeHtml } from "../client/utils/sanitize";
import { useQuery } from "wasp/client/operations";
import {
  getStudentSessionById,
  getStudentAnalyticsTrades,
  getStudentSessionAccounts,
  getMyStudents,
} from "wasp/client/operations";
import { useMemo } from "react";
import StudentLayout from "./StudentLayout";
import MentorNoteEditor from "./MentorNoteEditor";
import { Card, CardContent } from "../client/components/ui/card";
import { Button } from "../client/components/ui/button";
import { ArrowLeft, Clock, LucideYoutube } from "lucide-react";
import {
  getMoodEmoji,
  getMoodLabel,
  getPerformanceLabel,
  getPerformanceColor,
  formatSessionDuration,
  extractYouTubeId,
} from "../trading-sessions/sessionUtils";
import { SessionErrorBadges } from "../trading-sessions/SessionErrorBadges";
import { normalizeSymbol } from "../shared/utils";
import useTranslation from "../client/hooks/useTranslation";
import useLanguage from "../client/hooks/useLanguage";
import useColorMode, { isDarkBackground } from "../client/hooks/useColorMode";
import { SessionChart } from "../trading-sessions/SessionChart";
import { SessionAccountsTable } from "../trading-sessions/SessionAccountsTable";

function netPnl(trade: any): number {
  return (trade.pnl ?? 0) + (trade.commission ?? 0) + (trade.swaps ?? 0);
}

export default function StudentSessionDetailPage() {
  const t = useTranslation();
  const [language] = useLanguage();
  const [colorMode] = useColorMode();
  const isDark = isDarkBackground(colorMode);
  const { studentId, sessionId } = useParams<{ studentId: string; sessionId: string }>();
  const navigate = useNavigate();

  const { data: students = [], isLoading: loadingStudents } = useQuery(getMyStudents);
  const student = studentId ? students.find((s: any) => s.studentId === studentId) : undefined;
  const permissions = student?.permissions ?? {
    canViewJournal: false,
    canViewDashboard: false,
    canViewAnalytics: false,
    canViewSessions: false,
    canViewPropFirmManager: false,
  };

  const enabled = !!studentId && !!sessionId && !loadingStudents && permissions.canViewSessions;

  const { data: sessionRaw, refetch } = useQuery(
    getStudentSessionById,
    { studentId: studentId ?? "", sessionId: sessionId ?? "" },
    { enabled },
  );
  const session = sessionRaw as any;

  const { data: trades = [] } = useQuery(
    getStudentAnalyticsTrades,
    { studentId: studentId ?? "", excludeInactive: false },
    { enabled: enabled && permissions.canViewAnalytics },
  );

  const { data: sessionAccounts = [] } = useQuery(
    getStudentSessionAccounts,
    { studentId: studentId ?? "" },
    { enabled: enabled && permissions.canViewAnalytics },
  );

  const sessionPnl = useMemo(() => {
    if (!session?.startedAt || !session?.endedAt) return null;
    const start = new Date(session.startedAt).getTime();
    const end = new Date(session.endedAt).getTime();
    let pnl = 0;
    for (const trade of (trades as any[])) {
      const tradeTime = new Date(trade.exitDate ?? trade.date).getTime();
      if (tradeTime >= start && tradeTime <= end) pnl += netPnl(trade);
    }
    return parseFloat(pnl.toFixed(2));
  }, [session, trades]);

  const sessionTrades = useMemo(() => {
    if (!session?.startedAt || !session?.endedAt) return [];
    const start = new Date(session.startedAt).getTime();
    const end = new Date(session.endedAt).getTime();
    return (trades as any[]).filter((trade) => {
      const tradeTime = new Date(trade.exitDate ?? trade.date).getTime();
      return tradeTime >= start && tradeTime <= end;
    });
  }, [session, trades]);

  // Trades fed to the session chart: filtered on the trade ENTRY date (trade.date),
  // matching the trader-side SessionDetailPage chart so the marker set is identical.
  const chartTrades = useMemo(() => {
    if (!session?.startedAt || !session?.endedAt) return [];
    const start = new Date(session.startedAt).getTime();
    const end = new Date(session.endedAt).getTime();
    return (trades as any[]).filter((trade) => {
      const entryTime = new Date(trade.date).getTime();
      return entryTime >= start && entryTime <= end;
    });
  }, [session, trades]);

  const pnlByAccount = useMemo(() => {
    if (!session?.startedAt || !session?.endedAt) return [];
    const start = new Date(session.startedAt).getTime();
    const end = new Date(session.endedAt).getTime();
    const accountNameById = new Map<string, string>();
    for (const a of sessionAccounts as any[]) accountNameById.set(a.id, a.name);
    const byAccount = new Map<string | null, number>();
    for (const trade of trades as any[]) {
      const tradeTime = new Date(trade.exitDate ?? trade.date).getTime();
      if (tradeTime < start || tradeTime > end) continue;
      const key = (trade.accountId ?? null) as string | null;
      byAccount.set(key, (byAccount.get(key) ?? 0) + netPnl(trade));
    }
    return Array.from(byAccount.entries())
      .map(([accountId, pnl]) => ({
        accountId,
        name: accountId ? (accountNameById.get(accountId) ?? "—") : null,
        pnl: parseFloat(pnl.toFixed(2)),
      }))
      .sort((a, b) => b.pnl - a.pnl);
  }, [session, trades, sessionAccounts]);

  if (!studentId || !sessionId) return null;

  const sessionData = session;

  const sessionDate = sessionData?.startedAt
    ? new Date(sessionData.startedAt).toLocaleDateString(language, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  const videoId = sessionData?.videoUrl ? extractYouTubeId(sessionData.videoUrl) : null;

  return (
    <StudentLayout
      studentId={studentId}
      studentFirstName={student?.student?.firstName ?? null}
      studentLastName={student?.student?.lastName ?? null}
      permissions={permissions}
    >
      <div className="space-y-6">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/mentor/student/${studentId}/sessions`)}
          className="gap-2 text-muted-foreground hover:text-foreground -ml-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t.sessionDetail.back}
        </Button>

        {loadingStudents || !session ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <>
            {/* Hero header */}
            <div className="space-y-3">
              <div className="flex items-baseline gap-4 flex-wrap">
                <span
                  className="text-3xl font-bold"
                  style={{ fontFamily: "'Red Hat Display', sans-serif" }}
                >
                  {sessionData.name ?? sessionDate}
                </span>
                {sessionData.name && (
                  <span
                    className="text-3xl font-bold text-muted-foreground/50 capitalize shrink-0"
                    style={{ fontFamily: "'Red Hat Display', sans-serif" }}
                  >
                    {sessionDate}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                {sessionData.mood && (
                  <div className="flex items-center gap-2 bg-muted/50 rounded-xl px-4 py-2">
                    <span className="text-2xl">{getMoodEmoji(sessionData.mood)}</span>
                    <span className="text-sm font-medium">{getMoodLabel(sessionData.mood, t)}</span>
                  </div>
                )}
                {sessionData.performance && (
                  <div
                    className={`rounded-xl px-4 py-2 text-sm font-semibold bg-muted/50 ${getPerformanceColor(sessionData.performance)}`}
                  >
                    {getPerformanceLabel(sessionData.performance, t)}
                  </div>
                )}
                {(sessionData.errors ?? []).length > 0 && (
                  <SessionErrorBadges errors={sessionData.errors} />
                )}
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{formatSessionDuration(sessionData.actualDuration)}</span>
                </div>
                {sessionPnl !== null && (
                  <span
                    className={`text-3xl font-bold tabular-nums ${sessionPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}
                  >
                    {sessionPnl >= 0 ? "+" : ""}
                    {sessionPnl.toFixed(2)} $
                  </span>
                )}
              </div>
            </div>

            {/* Main layout */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 items-start">
              {/* LEFT */}
              <div className="space-y-6">
                {/* Mentor note */}
                <Card className="overflow-hidden border-primary/30">
                  <div className="px-5 pt-4 pb-2 border-b border-primary/20 bg-primary/5">
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-primary">
                      {t.mentor.mentorNotes}
                    </p>
                  </div>
                  <CardContent className="p-4">
                    <MentorNoteEditor
                      studentId={studentId}
                      sessionId={sessionId}
                      existingNote={sessionData.mentorNotes?.[0]}
                      onSaved={() => refetch()}
                    />
                  </CardContent>
                </Card>

                {/* Video */}
                {videoId && (
                  <Card className="overflow-hidden">
                    <div className="px-5 pt-4 pb-2 border-b border-border/50 flex items-center gap-2">
                      <LucideYoutube className="w-4 h-4 text-primary/70" />
                      <p className="text-[11px] font-semibold tracking-widest uppercase text-primary/70">
                        {t.sessionDetail.videoTitle}
                      </p>
                    </div>
                    <CardContent className="p-0">
                      <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                        <iframe
                          className="absolute inset-0 w-full h-full"
                          src={`https://www.youtube-nocookie.com/embed/${videoId}`}
                          title={t.sessionDetail.videoTitle}
                          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Chart + comptes tradés (parité avec la vue trader) */}
                {permissions.canViewAnalytics && (
                  <>
                    <SessionChart
                      session={sessionData}
                      trades={chartTrades}
                      accounts={sessionAccounts as any[]}
                      isDark={isDark}
                    />
                    <SessionAccountsTable
                      accounts={sessionAccounts as any[]}
                      pnlByAccount={pnlByAccount}
                      t={t}
                    />
                  </>
                )}

                {/* Notes de l'élève */}
                {sessionData.notes && (
                  <Card className="overflow-hidden">
                    <div className="px-5 pt-4 pb-2 border-b border-border/50">
                      <p className="text-[11px] font-semibold tracking-widest uppercase text-primary/70">
                        {t.sessionDetail.notes}
                      </p>
                    </div>
                    <CardContent className="p-5">
                      <div
                        className="prose prose-sm dark:prose-invert max-w-none text-sm text-foreground"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(sessionData.notes) }}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Trades */}
                {permissions.canViewAnalytics && (
                  <Card className="overflow-hidden">
                    <div className="px-5 pt-4 pb-3 border-b border-border/50">
                      <p className="text-[11px] font-semibold tracking-widest uppercase text-primary/70">
                        {t.sessionDetail.trades}
                      </p>
                    </div>
                    <CardContent className="p-4">
                      {sessionTrades.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t.sessionDetail.noTrades}</p>
                      ) : (
                        <div className="space-y-2">
                          {sessionTrades.map((trade: any) => (
                            <div
                              key={trade.id}
                              className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 text-sm"
                            >
                              <div className="flex items-center gap-3">
                                <span
                                  className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                    trade.type === "long"
                                      ? "bg-emerald-500/10 text-emerald-500"
                                      : "bg-red-500/10 text-red-500"
                                  }`}
                                >
                                  {trade.type === "long" ? t.journal.long : t.journal.short}
                                </span>
                                <span className="font-mono font-semibold">
                                  {normalizeSymbol(trade.symbol)}
                                </span>
                                <span className="text-muted-foreground text-xs">
                                  {new Date(trade.date).toLocaleDateString()}
                                </span>
                              </div>
                              {trade.pnl !== null && (
                                <span
                                  className={`font-mono font-semibold tabular-nums text-xs ${
                                    trade.pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"
                                  }`}
                                >
                                  {trade.pnl >= 0 ? "+" : ""}
                                  {trade.pnl.toFixed(2)} $
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* RIGHT — sidebar */}
              <div className="space-y-4">
                {/* Timing */}
                <Card>
                  <div className="px-4 pt-4 pb-2">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                      {t.sessionDetail.timing}
                    </p>
                  </div>
                  <CardContent className="px-4 pb-4 space-y-3">
                    <div>
                      <p className="text-[11px] text-muted-foreground mb-1">{t.sessionDetail.startedAt}</p>
                      <p className="text-sm">
                        {sessionData.startedAt
                          ? new Date(sessionData.startedAt).toLocaleString(language)
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground mb-1">{t.sessionDetail.endedAt}</p>
                      <p className="text-sm">
                        {sessionData.endedAt
                          ? new Date(sessionData.endedAt).toLocaleString(language)
                          : "—"}
                      </p>
                    </div>
                    <div className="flex gap-6 pt-1">
                      <div>
                        <p className="text-[11px] text-muted-foreground">{t.sessionDetail.duration}</p>
                        <p className="text-sm font-semibold mt-0.5">
                          {formatSessionDuration(sessionData.actualDuration)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Evaluation */}
                <Card>
                  <div className="px-4 pt-4 pb-2">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                      {t.sessionDetail.evaluation}
                    </p>
                  </div>
                  <CardContent className="px-4 pb-4 space-y-3">
                    <div>
                      <p className="text-[11px] text-muted-foreground mb-1">{t.session.mood}</p>
                      <p className="text-sm">
                        {sessionData.mood
                          ? `${getMoodEmoji(sessionData.mood)} ${getMoodLabel(sessionData.mood, t)}`
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground mb-1">{t.session.performance}</p>
                      <p className={`text-sm font-semibold ${getPerformanceColor(sessionData.performance)}`}>
                        {sessionData.performance
                          ? getPerformanceLabel(sessionData.performance, t)
                          : "—"}
                      </p>
                    </div>
                    {sessionData.mentalState && (
                      <div>
                        <p className="text-[11px] text-muted-foreground mb-1">{t.sessionDetail.mentalState}</p>
                        <p className="text-sm">{sessionData.mentalState}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Metrics */}
                <Card>
                  <div className="px-4 pt-4 pb-2">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                      {t.sessionDetail.metrics}
                    </p>
                  </div>
                  <CardContent className="px-4 pb-4 space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-border/50">
                      <p className="text-[11px] text-muted-foreground">{t.sessionDetail.pnl}</p>
                      <p
                        className={`text-base font-bold tabular-nums ${
                          sessionPnl === null
                            ? "text-muted-foreground"
                            : sessionPnl >= 0
                            ? "text-pnlPositive"
                            : "text-pnlNegative"
                        }`}
                      >
                        {sessionPnl === null
                          ? t.sessionDetail.noValue
                          : `${sessionPnl >= 0 ? "+" : ""}${sessionPnl.toFixed(2)} $`}
                      </p>
                    </div>
                    {sessionData.maxRunup != null && (
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] text-muted-foreground">{t.sessionDetail.maxRunup}</p>
                        <p className="text-sm font-semibold text-pnlPositive">
                          ▲ {(sessionData.maxRunup as number).toFixed(2)} $
                        </p>
                      </div>
                    )}
                    {sessionData.maxDrawdown != null && (
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] text-muted-foreground">{t.sessionDetail.maxDrawdown}</p>
                        <p className="text-sm font-semibold text-pnlNegative">
                          ▼ {Math.abs(sessionData.maxDrawdown as number).toFixed(2)} $
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>
    </StudentLayout>
  );
}
